import {
  DatabaseConfig,
  initializeDatabase,
  putToStore,
  getFromStore,
  deleteFromStore,
  clearStore,
  wrapRequest
} from './indexedDBUtils';

const TYPE_IMAGE_CACHE_DB = 'ImageCacheDB';
const TYPE_IMAGE_STORE = 'images';
const CACHE_TTL_MS = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds

interface ICachedImage {
  key: string;
  blob: Blob;
  timestamp: number;
}

const inMemoryImageCache: Record<string, ICachedImage> = {};

const dbConfig: DatabaseConfig = {
  dbName: TYPE_IMAGE_CACHE_DB,
  version: 1,
  storeName: TYPE_IMAGE_STORE,
  keyPath: 'key',
  indexes: [
    {
      name: 'timestamp',
      keyPath: 'timestamp',
      options: { unique: false }
    }
  ]
};

async function getImageCacheDB(): Promise<IDBDatabase> {
  return initializeDatabase(dbConfig);
}

export async function saveImageToCache(key: string, blob: Blob): Promise<void> {
  const db = await getImageCacheDB();

  try {
    const imageEntry: ICachedImage = { key, blob, timestamp: Date.now() };
    await putToStore(db, TYPE_IMAGE_STORE, imageEntry);
    inMemoryImageCache[key] = imageEntry;
  } catch (error) {
    throw new Error(`Failed to save image to cache: ${error}`);
  } finally {
    db.close();
  }
}

export async function getImageFromCache(key: string): Promise<Blob | null> {
  const currentTime = Date.now();

  if (inMemoryImageCache[key]) {
    const { blob, timestamp } = inMemoryImageCache[key];
    if (currentTime - timestamp > CACHE_TTL_MS) {
      delete inMemoryImageCache[key];
      return null;
    }
    return blob;
  }

  const db = await getImageCacheDB();

  try {
    const cachedImage = await getFromStore<ICachedImage>(db, TYPE_IMAGE_STORE, key);
    if (!cachedImage) return null;

    if (currentTime - cachedImage.timestamp > CACHE_TTL_MS) {
      return null;
    }

    inMemoryImageCache[key] = cachedImage;
    return cachedImage.blob;
  } catch (error) {
    throw new Error(`Failed to get image from cache: ${error}`);
  } finally {
    db.close();
  }
}

export async function deleteImageFromCache(key: string): Promise<void> {
  const db = await getImageCacheDB();

  try {
    await deleteFromStore(db, TYPE_IMAGE_STORE, key);
    delete inMemoryImageCache[key];
  } catch (error) {
    throw new Error(`Failed to delete image from cache: ${error}`);
  } finally {
    db.close();
  }
}

export async function getExpiredImages(): Promise<ICachedImage[]> {
  const cutoff = Date.now() - CACHE_TTL_MS;
  const db = await getImageCacheDB();
  const transaction = db.transaction(TYPE_IMAGE_STORE, 'readwrite');
  const store = transaction.objectStore(TYPE_IMAGE_STORE);
  const index = store.index('timestamp');
  const range = IDBKeyRange.upperBound(cutoff, true);
  const results: ICachedImage[] = [];

  try {
    return new Promise((resolve, reject) => {
      const cursorRequest = index.openCursor(range);

      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (cursor) {
          results.push(cursor.value as ICachedImage);
          cursor.delete();
          cursor.continue();
        } else {
          resolve(results);
        }
      };

      cursorRequest.onerror = () => {
        reject(new Error(`Failed to query expired images: ${cursorRequest.error}`));
      };
    });
  } catch (error) {
    throw new Error(`Failed to get expired images: ${error}`);
  } finally {
    db.close();
  }
}

export async function clearImageCache(): Promise<void> {
  const db = await getImageCacheDB();

  try {
    await clearStore(db, TYPE_IMAGE_STORE);
    Object.keys(inMemoryImageCache).forEach(key => delete inMemoryImageCache[key]);
  } catch (error) {
    throw new Error(`Failed to clear image cache: ${error}`);
  } finally {
    db.close();
  }
}
